/**
 * "Esperando começar" — PROJETOS-LIMPO 2 (14/09/2026).
 *
 * Era a página própria `/admin/projetos/pendentes` (P1). Virou a PRIMEIRA seção da lista de
 * projetos: uma porta só. As vendas ganhas que viraram projeto e ainda não foram iniciadas,
 * com o prazo para iniciar (Configurações) e o botão Iniciar — o mesmo diálogo de escolher o
 * tipo (B2) quando o projeto nasceu "A definir".
 *
 * ❌ Iniciar NÃO gera mais tarefas: as Atividades padrão morreram em 14/09 (decisão do
 * Matheus). O cronograma nasce no levantamento técnico.
 *
 * 15/09 (A-OBRA-PRECISA-DIZER-COMO-VAI): sem ninguém esperando, a seção NÃO aparece — "seção
 * vazia não ocupa espaço". O número "Esperando começar" do painel continua dizendo 0.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogContent,
  DialogFooter,
  HelpTip,
  Label,
  Select,
  useConfirmDialog,
  useToast,
} from '@/components/ui';
import { useProjetosPendentes, useIniciarProjeto, useProjetoTipos, type ProjetoPendente } from '@/lib/projetos-store';
import { formatDate } from '@/lib/format-date';
import { cent } from './obra-formatos';

// B2: placeholder do tipo (Migration 174) — projeto nasce "A definir".
const TIPO_A_DEFINIR = 'ptipo-a-definir';

export function EsperandoComecar() {
  const navigate = useNavigate();
  const pendentesQuery = useProjetosPendentes();
  const items = pendentesQuery.data?.items ?? [];
  const confirm = useConfirmDialog();
  const toast = useToast();
  const iniciar = useIniciarProjeto();
  const [escolherTipo, setEscolherTipo] = useState<ProjetoPendente | null>(null);

  async function handleIniciar(p: ProjetoPendente) {
    if (p.tipoId === TIPO_A_DEFINIR) {
      setEscolherTipo(p);
      return;
    }
    const ok = await confirm({
      title: 'Iniciar projeto?',
      message: `Isso marca "${p.nome}" como em andamento. O cronograma nasce no levantamento técnico, dentro do projeto.`,
      confirmLabel: 'Iniciar',
    });
    if (!ok) return;
    try {
      await iniciar.mutateAsync({ projetoId: p.id });
      navigate(`/admin/projetos/${p.id}`);
    } catch {
      toast.show('Não foi possível iniciar o projeto. Tente novamente.', { variant: 'error' });
    }
  }

  // Carregou e não tem ninguém: a seção some (o painel do topo já diz "0").
  if (pendentesQuery.data && items.length === 0 && !escolherTipo) return null;

  return (
    <section className="space-y-2" data-testid="esperando-comecar">
      <h2 className="inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wider text-ink-2 dark:text-ink-dark-2">
        Esperando começar{pendentesQuery.data ? ` (${items.length})` : ''}
        <HelpTip
          texto={`Vendas fechadas que viraram projeto e ainda não foram iniciadas. Os mais atrasados aparecem primeiro. O prazo para iniciar${pendentesQuery.data ? ` (hoje ${pendentesQuery.data.slaDias} dias)` : ''} se ajusta em Configurações › Expediente e prazos. Iniciar marca o projeto como em andamento; o cronograma nasce no levantamento.`}
        />
      </h2>

      {pendentesQuery.isLoading ? (
        <Card className="p-4 animate-pulse" data-testid="pendentes-loading">
          <div className="h-4 w-1/2 rounded bg-line-soft dark:bg-paper-dark-warm" />
        </Card>
      ) : pendentesQuery.isError ? (
        <Card className="p-4 text-center" data-testid="pendentes-error">
          <p className="mb-2 text-sm text-red dark:text-red-glow">Não foi possível carregar os projetos que esperam começar.</p>
          <Button variant="ghost" size="sm" onClick={() => pendentesQuery.refetch()}>
            Tentar novamente
          </Button>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="pendentes-list">
          {items.map((p) => (
            <PendenteRow key={p.id} projeto={p} onIniciar={() => handleIniciar(p)} iniciando={iniciar.isPending} />
          ))}
        </div>
      )}

      {escolherTipo && (
        <EscolherTipoDialog
          projeto={escolherTipo}
          onClose={() => setEscolherTipo(null)}
          onIniciado={(id) => {
            setEscolherTipo(null);
            navigate(`/admin/projetos/${id}`);
          }}
        />
      )}
    </section>
  );
}

/**
 * B2: modal para escolher o tipo real do projeto "A definir" no momento de iniciar.
 * Iniciar sem tipo é bloqueado.
 */
function EscolherTipoDialog({
  projeto,
  onClose,
  onIniciado,
}: {
  projeto: ProjetoPendente;
  onClose: () => void;
  onIniciado: (projetoId: string) => void;
}) {
  const tiposQuery = useProjetoTipos(true); // só ativos
  const tipos = tiposQuery.data?.items ?? [];
  const [tipoId, setTipoId] = useState('');
  const iniciar = useIniciarProjeto();
  const toast = useToast();

  async function handleConfirmar() {
    if (!tipoId) return;
    try {
      await iniciar.mutateAsync({ projetoId: projeto.id, tipoId });
      onIniciado(projeto.id);
    } catch (err) {
      const msg =
        err instanceof Error && err.message.includes('exige-tipo')
          ? 'Escolha um tipo válido pra iniciar.'
          : 'Não foi possível iniciar o projeto. Tente novamente.';
      toast.show(msg, { variant: 'error' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="md">
      <DialogHeader>
        <DialogTitle>Iniciar projeto</DialogTitle>
        <DialogDescription>
          Escolha o tipo de “{projeto.nome}”. O projeto inicia sem etapas — o cronograma é montado no levantamento técnico.
        </DialogDescription>
      </DialogHeader>
      <DialogContent className="space-y-4">
        <div>
          <Label htmlFor="iniciar-tipo">Tipo do projeto</Label>
          <Select id="iniciar-tipo" data-testid="iniciar-tipo-select" value={tipoId} onChange={(e) => setTipoId(e.target.value)}>
            <option value="" disabled>
              Selecione o tipo…
            </option>
            {tipos.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome}
              </option>
            ))}
          </Select>
          {!tiposQuery.isLoading && tipos.length === 0 && (
            <p className="mt-1.5 text-xs text-red" data-testid="iniciar-sem-tipos">
              Nenhum tipo de projeto cadastrado. Crie um tipo em Catálogos antes de iniciar.
            </p>
          )}
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={iniciar.isPending}>
          Cancelar
        </Button>
        <Button onClick={handleConfirmar} disabled={!tipoId || iniciar.isPending} data-testid="iniciar-confirmar">
          {iniciar.isPending ? 'Iniciando…' : 'Iniciar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function PendenteRow({ projeto, onIniciar, iniciando }: { projeto: ProjetoPendente; onIniciar: () => void; iniciando: boolean }) {
  const vencido = projeto.slaVencido;
  return (
    <Card className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`pendente-row-${projeto.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate font-semibold text-ink-1 dark:text-ink-dark-1">
            {projeto.numeroHumano != null && <span className="mr-1 font-mono text-ink-3 dark:text-ink-dark-3">#{projeto.numeroHumano}</span>}
            {projeto.nome}
          </h3>
          {vencido ? (
            <Badge variant="danger" className="animate-pulse" data-testid="badge-sla-vencido">
              Prazo vencido
            </Badge>
          ) : (
            <Badge variant="neutral">{projeto.diasRestantes === 1 ? 'Falta 1 dia' : `Faltam ${projeto.diasRestantes} dias`}</Badge>
          )}
        </div>
        <div className="mt-1 space-y-0.5 text-xs text-ink-3 dark:text-ink-dark-3">
          {projeto.clienteRazaoSocial && (
            <div className="flex min-w-0 items-center gap-1">
              <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{projeto.clienteRazaoSocial}</span>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-1">
            {/* #309: o vendido é a venda CONGELADA no Ganho — nunca a coluna legada. */}
            <span className="font-mono tabular-nums">{cent(projeto.valorVendidoCentavos)}</span>
            <span>· venda ganha em {formatDate(projeto.criadoEmIso)}</span>
          </div>
        </div>
      </div>
      <Button onClick={onIniciar} disabled={iniciando} data-testid={`btn-iniciar-${projeto.id}`}>
        Iniciar
      </Button>
    </Card>
  );
}
